import React, { useState, useEffect } from 'react';
import API from '../http/API';
import {useNavigate} from "react-router-dom";
import { useOrgState, useCourt, useRequestUrl, useForwardingOrg, useCaptchaKey } from '../context/contextHooks';
import { useStyles } from "../styles/styles";
import useTheme from "@mui/material/styles/useTheme";
import useMediaQuery from '@mui/material/useMediaQuery'
import { getSelectStates } from "./homeFunctions";
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import HaveAgency from "./HaveAgency";
import Options from './Options';

const Home = () => {
    const classes = useStyles;
    const theme = useTheme();
    const smallHeader = useMediaQuery(theme.breakpoints.down('lg'));
    const navigate = useNavigate();
    const [requestUrl,] = useRequestUrl();
    const [orgState, ] = useOrgState();
    const [forwardingOrg, setForwardingOrg] = useForwardingOrg();
    const [court, setCourt] = useCourt();
    const [captchaKey, setCaptchaKey] = useCaptchaKey();
    const [loading, setLoading] = useState(false);
    const [actionResponse, setActionResponse] = useState(null);
    const [orgs, setOrgs] = useState(null);
    const [selectedOrg, setSelectedOrg] = useState(null);
    const [courtStates, setCourtStates] = useState(null);

    const clearMessaging = () => {
        setActionResponse(null);
    }

    useEffect(() => {
        document.title = 'Select Court - Neumo Payments';
    }, []);

    const getCourtStates = () => {
        clearMessaging();
        setLoading(true);
        const url = `${requestUrl}/getCourtStates`;
        API.getRetrieve(url, 'getCourtStates', null, setActionResponse);
    }

    const getOrgs = (state) => {
        clearMessaging();
        setSelectedOrg(null);
        if (state) {
            setLoading(true);
            if (!orgState || state !== orgState) {
                const url = `${requestUrl}/getOrgs/${state}`;
                API.getRetrieve(url, 'getOrgs', null, setActionResponse);
            } else {
                setOrgs(null);
            }
        }
    }

    const getCaptchaKey = () => {
        clearMessaging();
        setLoading(true);
        const url = `${requestUrl}/getCaptchaKey`;
        API.getRetrieve(url, 'getCaptchaKey', null, setActionResponse);
    }

    const showNext = (destination) => {
        if (destination) {
            if (destination.key === 'balances') {
                navigate('/balanceLookup');
            } else if (destination.key === 'miscFees') {
                navigate('/miscFees');
            }
        } else {
            setActionResponse({error: true, errorMessage: 'No payment type has been selected'});
        }
    }

    useEffect(() => {
        if (!loading && !courtStates) {
            getCourtStates();
        }
    }, [courtStates, loading])

    useEffect(() => {
        if (!loading && !captchaKey && (!actionResponse || !actionResponse.error)) {
            getCaptchaKey();
        }
    }, [captchaKey, loading])

    useEffect(() => {
        setCourt(selectedOrg);
    }, [selectedOrg])

    useEffect(() => {
        if (court && court.landingPageConfig && selectedOrg && !forwardingOrg) {
            const state = {court: court};
            navigate('/home', {state: state});
        }
    }, [court])

    useEffect(() => {
        if (loading && actionResponse) {
            if (!actionResponse.error) {
                if (actionResponse.requestType === 'getOrgs' && actionResponse.data) {
                    setOrgs(actionResponse.data);
                } else if (actionResponse.requestType === 'getCourtStates') {
                    setCourtStates(getSelectStates(actionResponse.data));
                } else if (actionResponse.requestType === 'getOrg') {
                    if (actionResponse.data) {
                        setSelectedOrg(actionResponse.data);
                        setForwardingOrg(actionResponse.data);
                    }
                } else if (actionResponse.requestType === 'getCaptchaKey') {
                    setCaptchaKey(actionResponse.data);
                }
            }
            setLoading(false);
        }
    }, [loading, actionResponse])

    return (
        <>
            <Typography variant={smallHeader ? 'h5' : 'h4'} component="h1" color='primary' sx={{...classes.title, marginTop: '1rem'}}>Welcome</Typography>
            <Grid
                container
                spacing={3}
                justifyContent="center"
            >
                <Box component={Grid} item xs={12} sm={6} md={5} justifyItems='center' display={{xs: 'none', sm: 'block'}}>
                    <List sx={{width: '100%', marginTop: `${smallHeader ? '0' : '1rem'}`}} >
                        <ListItem alignItems='center' sx={classes.listPadding}>
                            <ListItemText
                                sx={{padding: '0 1rem'}}
                                primary={
                                    <Typography
                                        variant={smallHeader ? 'subtitle2' : 'subtitle1'}
                                        component="span"
                                        sx={{
                                            fontSize: smallHeader ? '' : '1.2rem',
                                            lineHeight: smallHeader ? '1.2rem' : '1.4rem'
                                        }}
                                    >
                                        To make a payment to a court or government agency, follow these steps:
                                    </Typography>
                                }
                            />
                        </ListItem>
                        <ListItem alignItems='center' sx={classes.listPadding}>
                            <ListItemIcon sx={{padding: '0 1rem'}}>
                                <ChevronRightIcon color='secondary' sx={{fontSize: `${smallHeader ? '24pt' : '32pt'}`}} />
                            </ListItemIcon>
                            <ListItemText
                                sx={{padding: '0 1rem'}}
                                primary={
                                    <Typography
                                        variant={smallHeader ? 'subtitle2' : 'subtitle1'}
                                        component="span"
                                        sx={{fontSize: smallHeader ? '' : '1.2rem', lineHeight: smallHeader ? '1.2rem' : '1.4rem'}}
                                    >
                                        Enter the state and name of the organization you want to pay
                                    </Typography>
                                }
                            />
                        </ListItem>
                        <ListItem alignItems='center' sx={classes.listPadding}>
                            <ListItemIcon sx={{padding: '0 1rem'}}>
                                <ChevronRightIcon color='secondary' sx={{fontSize: `${smallHeader ? '24pt' : '32pt'}`}} />
                            </ListItemIcon>
                            <ListItemText
                                sx={{padding: '0 1rem'}}
                                primary={
                                    <Typography
                                        variant={smallHeader ? 'subtitle2' : 'subtitle1'}
                                        component="span"
                                        sx={{fontSize: smallHeader ? '' : '1.2rem', lineHeight: smallHeader ? '1.2rem' : '1.4rem'}}
                                    >
                                        Select what you are paying for
                                    </Typography>
                                }
                            />
                        </ListItem>
                        <ListItem alignItems='center' sx={classes.listPadding}>
                            <ListItemIcon sx={{padding: '0 1rem'}}>
                                <ChevronRightIcon color='secondary' sx={{fontSize: `${smallHeader ? '24pt' : '32pt'}`}} />
                            </ListItemIcon>
                            <ListItemText
                                sx={{padding: '0 1rem'}}
                                primary={
                                    <Typography
                                        variant={smallHeader ? 'subtitle2' : 'subtitle1'}
                                        component="span"
                                        sx={{fontSize: smallHeader ? '' : '1.2rem', lineHeight: smallHeader ? '1.2rem' : '1.4rem'}}
                                    >
                                        Enter the information requested to complete your payment
                                    </Typography>
                                }
                            />
                        </ListItem>
                    </List>
                </Box>
                <Grid item xs={12} sm={6} md={7} sx={classes.gridBottom}>
                    {actionResponse && actionResponse.error &&
                        <Alert severity='error'>
                            <AlertTitle>Error</AlertTitle>
                            {actionResponse.errorMessage}
                        </Alert>
                    }
                    { courtStates &&
                        <>
                            { (forwardingOrg) ?
                                <HaveAgency showNext={showNext} /> :
                                <Options
                                    courtStates={courtStates}
                                    getOrgs={getOrgs}
                                    orgs={orgs}
                                    court={court}
                                    setSelectedOrg={setSelectedOrg}
                                    showNext={showNext}
                                    setActionResponse={setActionResponse}
                                />
                            }
                        </>
                    }
                </Grid>
            </Grid>
        </>
    );
}

export default Home;
